import React, { ReactNode } from 'react';
import { Component } from 'react';
import { StateType } from '@app/states';
import { lazyInject, TYPES, fmtMsg, TemplateBackgroundImageLayout } from '@app/util';
import { ISchoolService } from '@app/service/schools';
import { IContentResourceService, ITrainingResourceService } from '@app/service/chat';
import { Icon, Spin, List, message, Select, Input, Row, Col, Tabs } from 'antd-min';
import InfiniteScroll from 'react-infinite-scroller';
import { GLGlobal } from 'gl-commonui';
import { CONSTS } from '@app/util';
import { GSAdminLocale } from '@app/locales/localeid';
import { IUserService } from '@app/service/users';
import { IRoleService } from '@app/service/admin/roles';

interface ResourceModel {
    regionId: string;
    schoolId: string;
    teacherId: string;
}

interface ContentReourceModel {
    groupType: string
    resourceName: string;
    unit: number;
    type: string;
    resourceUrl?: string;
    links: ResourceUrlModel[];
}

interface TrainingResourceModel {
    resourceName: string;
    description: string;
    resourceUrl: string;
}

interface ResourceUrlModel {
    sharableMediaType: number;
    url: string;
}

interface UserResourcePermission {
    gsUnit: number;
    lsUnit: number;
    lsversiongroup: string;
    gsversiongroup: string;
}

interface UserRole {
    id: string;
    name: string;
}

interface SearchResourceProps {
    resourcePanelVisible: boolean
    onResourceSelect: (resource: any) => void
    resourceModel: ResourceModel
}

export const enum ContentMediaType {
    CM = 1,
    Song = 2,
    Video = 3
}

export const enum TrainingResourceType {
    NoSearchResult = 0,
    Course = 1,
    Series = 2,
    Group = 3,
    CourseQuestionnaire = 4,
    CourseVideo = 5,
    CourseFile = 6,
    SeriesQuestionnaire = 7,
    SeriesVideo = 8,
    SeriesFile = 9,
    GroupQuestionnaire = 10,
    GroupVideo = 11,
    GroupFile = 12
}

export const SearchResource: React.FC<SearchResourceProps> = ({resourcePanelVisible, onResourceSelect, resourceModel}) => {
    return (
        resourcePanelVisible &&
        <ResourceList
            handleOnResourceSelect={onResourceSelect}
            resourceModel={resourceModel}
        />
    )
}
interface IResourceListProps {
    handleOnResourceSelect: any;
    resourceModel: ResourceModel;
}

const CONTENT_SEARCH_PAGE_SIZE = 10;

interface IResourceListState {
    resourceLoading: boolean;
    contentResourceList: ContentReourceModel[];
    contentPageIndex: number;
    contentHasMore: boolean;
    trainingResourceList: TrainingResourceModel[];
    localContentResourceList: ContentReourceModel[];
}

export class ResourceList extends Component<IResourceListProps, IResourceListState> {
    @lazyInject(TYPES.ISchoolService)
    schoolService: ISchoolService
    @lazyInject(TYPES.IContentResourceService)
    contentResourceService: IContentResourceService
    @lazyInject(TYPES.ITrainingResourceService)
    trainingResourceService: ITrainingResourceService
    @lazyInject(TYPES.IUserService)
    userService: IUserService
    @lazyInject(TYPES.IRoleService)
    roleService: IRoleService
    userResourcePermission?: UserResourcePermission;
    roles?: UserRole[];
    contentKeyword = '';
    // Bumped on every new content search; responses from an older search are dropped.
    contentSearchId = 0;
    constructor(props: IResourceListProps) {
        super(props);

        this.state = {
            resourceLoading: false,
            contentResourceList: [],
            contentPageIndex: 1,
            contentHasMore: false,
            trainingResourceList: [],
            localContentResourceList: []
        }
    }

    componentDidMount() {
        this.setState({resourceLoading: true});

        const { resourceModel: { schoolId, teacherId } } = this.props;
        this.schoolService.getTeacherContentPermission({
            schoolId: schoolId,
            teacherId: teacherId
        }).then(data => {
            if (data) {
                this.userResourcePermission = {
                    gsUnit: data.gsUnit,
                    lsUnit: data.lsUnit,
                    lsversiongroup: data.lsVersionGroupId,
                    gsversiongroup: data.gsVersionGroupId
                }
            }
        });

        this.userService.getUserById({id: this.props.resourceModel.teacherId}).then(data => {
            data && this.mapRolesIdWithName(data.roles);
        });
    }

    mapRolesIdWithName(roleNames: string[]) {
        this.roleService.getRoles().then(data => {
            this.roles = data.filter(r => roleNames.includes(r.name));

            this.setState({resourceLoading: false});
        });
    }

    /** Fetches one page of one scope (GS "Unit" or "LS"); resolves whether that scope has more pages. */
    async searchContentResource(searchId: number, keyword, maxUnit, versionGroupId, groupType, pageIndex: number): Promise<boolean> {
        if (!maxUnit || !versionGroupId) {
            return false;
        }

        const response = await this.contentResourceService.getResources({
            keyword: keyword,
            regionId: this.props.resourceModel.regionId,
            versionGroupId: versionGroupId,
            maxUnit: Math.abs(maxUnit),
            pageSize: CONTENT_SEARCH_PAGE_SIZE,
            pageIndex: pageIndex,
        });

        if (searchId !== this.contentSearchId) {
            return false;
        }

        const contents = this.parseContentData((response && response.data) || [], groupType);

        this.setState(prev => ({
            contentResourceList: [
                ...prev.contentResourceList,
                ...contents
            ]
        }));

        return !!response && response.totalPages > pageIndex;
    }

    loadContentPage = async (pageIndex: number) => {
        const searchId = this.contentSearchId;
        const keyword = this.contentKeyword;
        const { gsUnit, gsversiongroup, lsUnit, lsversiongroup } = this.userResourcePermission;

        this.setState({ resourceLoading: true, contentHasMore: false });

        try {
            const gsHasMore = await this.searchContentResource(searchId, keyword, gsUnit, gsversiongroup, "Unit", pageIndex);
            const lsHasMore = await this.searchContentResource(searchId, keyword, lsUnit, lsversiongroup, "LS", pageIndex);

            if (searchId === this.contentSearchId) {
                this.setState({ contentPageIndex: pageIndex, contentHasMore: gsHasMore || lsHasMore });
            }
        } catch {
            // Service layer already shows the error notification.
        } finally {
            if (searchId === this.contentSearchId) {
                this.setState({ resourceLoading: false });
            }
        }
    }

    handleOnContentLoadMore = () => {
        this.loadContentPage(this.state.contentPageIndex + 1);
    }

    async searchTrainingResource(keyword, versionGroupIds) {
        if (keyword && versionGroupIds && versionGroupIds.length != 0) {
            const data = await this.trainingResourceService.getCourses({
                id: this.props.resourceModel.teacherId,
                roles: this.roles,
                ids: versionGroupIds,
                searchText: keyword
            })

            if (data) {
                const contents = data.map(course => this.getTrainingResource(course));

                this.setState({ trainingResourceList: [ ...contents ] });
            }
        }
    }

    async searchLocalContentResource(keyword, versionGroupId, groupType) {
        if (versionGroupId) {
            const data = await this.contentResourceService.getLocalResources({
                keyword: keyword,
                regionId: this.props.resourceModel.regionId,
                versionGroupId: versionGroupId,
                teacherId: this.props.resourceModel.teacherId
            })

            if (data) {
                const contents = this.parseContentData(data, groupType);

                this.setState({
                    localContentResourceList: [
                        ...this.state.localContentResourceList,
                        ...contents
                    ]
                });
            }
        }
    }

    handleOnContentSearch = async (keyword: string) => {
        if (this.userResourcePermission && keyword.trim()) {
            this.contentSearchId++;
            this.contentKeyword = keyword;
            this.setState({ contentResourceList: [] });

            await this.loadContentPage(1);
        }
    }

    handleOnTrainingCourseSearch = async (keyword: string) => {
        if (this.userResourcePermission && keyword.trim()) {
            this.setState({
                trainingResourceList: [],
                resourceLoading: true
            });

            let versionGroupIds = [];
            const { gsUnit, lsUnit, lsversiongroup, gsversiongroup } = this.userResourcePermission;

            gsUnit && versionGroupIds.push(gsversiongroup);
            lsUnit && versionGroupIds.push(lsversiongroup);

            try {
                await this.searchTrainingResource(keyword, versionGroupIds);
            } catch {
                // Service layer already shows the error notification.
            } finally {
                setTimeout(() => this.setState({ resourceLoading: false }), 1000);
            }
        }
    }

    handleOnLocalContentSearch = async (keyword: string) => {
        if (this.userResourcePermission && keyword.trim()) {
            this.setState({
                localContentResourceList: [],
                resourceLoading: true
            });

            const { gsversiongroup } = this.userResourcePermission;

            try {
                await this.searchLocalContentResource(keyword, gsversiongroup, "Unit");
            } catch {
                // Service layer already shows the error notification.
            } finally {
                this.setState({ resourceLoading: false });
            }
        }
    }

    parseContentData = (sourceData: any, groupType: string) => {
        return sourceData.map((item) => {
            const model: ContentReourceModel = {
                groupType: groupType,
                resourceName: item.title,
                links: item.links,
                unit: item.unit,
                type: item.contentTypeName
            }
            return model
        });
    }
    getTrainingResource(resource) {
        const trainingDomain = (GLGlobal.processEnv().portals as any).trainer;
        const {searchType, courseId, courseVersionId, groupId, questionnaireId, seriesId, videoId, fileId} = resource;
        switch(searchType) {
            case TrainingResourceType.Course:
                return {
                    resourceName: resource.searchedText,
                    resourceUrl: `${trainingDomain}/course/${courseId}/${courseVersionId}/tableOfContents`
                }
            case TrainingResourceType.Series:
                return {
                    resourceName: resource.searchedText,
                    description: resource.courseName,
                    resourceUrl: `${trainingDomain}/course/${courseId}/${courseVersionId}/tableOfContents?seriesId=${seriesId}`
                }
            case TrainingResourceType.Group:
                return {
                    resourceName: resource.searchedText,
                    description: `${resource.courseName} > ${resource.seriesName}`,
                    resourceUrl: `${trainingDomain}/course/${courseId}/${courseVersionId}/tableOfContents?seriesId=${seriesId}&groupId=${groupId}`
                }
            case TrainingResourceType.CourseQuestionnaire:
                return {
                    resourceName: resource.searchedText,
                    description: `${resource.courseName}`,
                    resourceUrl: `${trainingDomain}/player/${courseId}/?versionId=${courseVersionId}&questionnaireId=${questionnaireId}`
                }
            case TrainingResourceType.SeriesQuestionnaire:
                return {
                    resourceName: resource.searchedText,
                    description: `${resource.courseName} > ${resource.seriesName}`,
                    resourceUrl: `${trainingDomain}/player/${courseId}/?versionId=${courseVersionId}&seriesId=${seriesId}&questionnaireId=${questionnaireId}`
                }
            case TrainingResourceType.GroupQuestionnaire:
                return {
                    resourceName: resource.searchedText,
                    description: `${resource.courseName} > ${resource.seriesName} > ${resource.groupName} ${resource.unit ? ` > Unit ${resource.unit}` : ""}`,
                    resourceUrl: `${trainingDomain}/player/${courseId}/?versionId=${courseVersionId}&seriesId=${seriesId}&groupId=${groupId}&questionnaireId=${questionnaireId}`
                }
            case TrainingResourceType.CourseVideo:
                return {
                    resourceName: resource.searchedText,
                    description: `${resource.courseName}`,
                    resourceUrl: `${trainingDomain}/player/${courseId}/?versionId=${courseVersionId}&videoId=${videoId}`
                }
            case TrainingResourceType.SeriesVideo:
                return {
                    resourceName: resource.searchedText,
                    description: `${resource.courseName} > ${resource.seriesName}`,
                    resourceUrl: `${trainingDomain}/player/${courseId}/?versionId=${courseVersionId}&seriesId=${seriesId}&videoId=${videoId}`
                }
            case TrainingResourceType.GroupVideo:
                return {
                    resourceName: resource.searchedText,
                    description: `${resource.courseName} > ${resource.seriesName} > ${resource.groupName} ${resource.unit ? ` > Unit ${resource.unit}` : ""}`,
                    resourceUrl: `${trainingDomain}/player/${courseId}/?versionId=${courseVersionId}&seriesId=${seriesId}&groupId=${groupId}&videoId=${videoId}`
                }
            case TrainingResourceType.CourseFile:
                return {
                    resourceName: resource.searchedText,
                    description: `${resource.fileName}`,
                    resourceUrl: `${trainingDomain}/player/${courseId}/?versionId=${courseVersionId}&fileId=${fileId}`
                }
            case TrainingResourceType.SeriesFile:
                return {
                    resourceName: resource.searchedText,
                    description: `${resource.courseName} > ${resource.seriesName}`,
                    resourceUrl: `${trainingDomain}/player/${courseId}/?versionId=${courseVersionId}&seriesId=${seriesId}&fileId=${fileId}`
                }
            case TrainingResourceType.GroupFile:
                return {
                    resourceName: resource.searchedText,
                    description: `${resource.courseName} > ${resource.seriesName} > ${resource.groupName}`,
                    resourceUrl: `${trainingDomain}/player/${courseId}/?versionId=${courseVersionId}&seriesId=${seriesId}&groupId=${groupId}&fileId=${fileId}`
                }
            default:
                return {};
        }
    }
    render() {
        const { handleOnResourceSelect } = this.props;
        const { TabPane } = Tabs;
        return (
            <div className="chat-resource-box">
                <Tabs defaultActiveKey="1">
                    <TabPane tab={fmtMsg({id: GSAdminLocale.ContentResource })} key="1">
                        <SearchableList
                            onSearch={this.handleOnContentSearch}
                            getTitle={(item: ContentReourceModel) => item.resourceName}
                            getDescription={(item: ContentReourceModel) => <span>{item.unit ? `${item.groupType} ${item.unit} - ${item.type}` : item.type}</span>}
                            getContent={(item: ContentReourceModel) => <ContentResourceType resource={item} onResourceSelect={handleOnResourceSelect} />}
                            content={this.state.contentResourceList}
                            loading={this.state.resourceLoading}
                            hasMore={this.state.contentHasMore}
                            onLoadMore={this.handleOnContentLoadMore}
                        />
                    </TabPane>
                    <TabPane tab={fmtMsg({id: GSAdminLocale.TrainingResource })} key="2">
                        <SearchableList
                            onSearch={this.handleOnTrainingCourseSearch}
                            getTitle={(item: TrainingResourceModel) => <a onClick={() => handleOnResourceSelect(item)}>{item.resourceName}</a>}
                            getDescription={(item: TrainingResourceModel) => item.description}
                            content={this.state.trainingResourceList}
                            loading={this.state.resourceLoading}
                        />
                    </TabPane>
                    <TabPane tab={fmtMsg({ id: GSAdminLocale.LocalContent })} key="3">
                        <SearchableList
                            onSearch={this.handleOnLocalContentSearch}
                            getTitle={(item: ContentReourceModel) => item.resourceName}
                            getDescription={(item: ContentReourceModel) => <span>{item.unit ? `${item.groupType} ${item.unit} - ${item.type}` : item.type}</span>}
                            getContent={(item: ContentReourceModel) => <ContentResourceType resource={item} onResourceSelect={handleOnResourceSelect} />}
                            content={this.state.localContentResourceList}
                            loading={this.state.resourceLoading}
                        />
                    </TabPane>
                </Tabs>
            </div>
        );
    }
}

export const contentResourceList = [
    {
        // CM || Pdf
        type: [1, 7],
        icon: <img src="data:image/png;base64,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" />,
    },
    {
        // TM
        type: [2],
        typeName: "TM",
        icon: <img src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABEAAAAWCAYAAAAmaHdCAAAAGXRFWHRTb2Z0d2FyZQBBZG9iZSBJbWFnZVJlYWR5ccllPAAAAyJpVFh0WE1MOmNvbS5hZG9iZS54bXAAAAAAADw/eHBhY2tldCBiZWdpbj0i77u/IiBpZD0iVzVNME1wQ2VoaUh6cmVTek5UY3prYzlkIj8+IDx4OnhtcG1ldGEgeG1sbnM6eD0iYWRvYmU6bnM6bWV0YS8iIHg6eG1wdGs9IkFkb2JlIFhNUCBDb3JlIDUuMy1jMDExIDY2LjE0NTY2MSwgMjAxMi8wMi8wNi0xNDo1NjoyNyAgICAgICAgIj4gPHJkZjpSREYgeG1sbnM6cmRmPSJodHRwOi8vd3d3LnczLm9yZy8xOTk5LzAyLzIyLXJkZi1zeW50YXgtbnMjIj4gPHJkZjpEZXNjcmlwdGlvbiByZGY6YWJvdXQ9IiIgeG1sbnM6eG1wPSJodHRwOi8vbnMuYWRvYmUuY29tL3hhcC8xLjAvIiB4bWxuczp4bXBNTT0iaHR0cDovL25zLmFkb2JlLmNvbS94YXAvMS4wL21tLyIgeG1sbnM6c3RSZWY9Imh0dHA6Ly9ucy5hZG9iZS5jb20veGFwLzEuMC9zVHlwZS9SZXNvdXJjZVJlZiMiIHhtcDpDcmVhdG9yVG9vbD0iQWRvYmUgUGhvdG9zaG9wIENTNiAoV2luZG93cykiIHhtcE1NOkluc3RhbmNlSUQ9InhtcC5paWQ6NzQ0M0EzMDFBMTBCMTFFODg1RjZEMkI4MUIzNzY0NjMiIHhtcE1NOkRvY3VtZW50SUQ9InhtcC5kaWQ6NzQ0M0EzMDJBMTBCMTFFODg1RjZEMkI4MUIzNzY0NjMiPiA8eG1wTU06RGVyaXZlZEZyb20gc3RSZWY6aW5zdGFuY2VJRD0ieG1wLmlpZDo3NDQzQTJGRkExMEIxMUU4ODVGNkQyQjgxQjM3NjQ2MyIgc3RSZWY6ZG9jdW1lbnRJRD0ieG1wLmRpZDo3NDQzQTMwMEExMEIxMUU4ODVGNkQyQjgxQjM3NjQ2MyIvPiA8L3JkZjpEZXNjcmlwdGlvbj4gPC9yZGY6UkRGPiA8L3g6eG1wbWV0YT4gPD94cGFja2V0IGVuZD0iciI/PmMlpegAAAEsSURBVHjaYoyOjvZhYGCYBcSSDITBNyCeDsTlQPwXJLBkyRIGJiA9l0gDQIALiIuBeBEQM8MEWYBYDMq2BeInODRvA2JNID4FxGZAHAUVTwDi3yxICkEGPMBhyC8oPR3qiilQg3hjYmJCmBhIB1OBOAvK9gXiOSwkGhAAxApQ9nkgNgTiCGIN+QOl/aEYGbASa0gZNAyQ1QtBvcNArCH7oBgZGMAMISdgMcDgMQQ9TFyAOA2JPwsqdhaIVyPJp+MzZA8QCwLxTCB2hWpeBcT3oIaEQg1SgmVAXN65B6XPIokJQg0IpSRMzkKz/2pKDJkF9cJqYgIWZqsJEt8E6kV02oCQS+5hYd/DIofhEj4gFiAhefAiG/IKWrpdJDOtvWKCJp7nZBoA0pcGEGAA5EQ6RiAsijgAAAAASUVORK5CYII=" />,
    },
    {
        // Audio
        type: [3],
        typeName: "Audio",
        icon: <Icon type="play-circle" />,
    },
    {
        // Video
        type: [4],
        typeName: "Video",
        icon: <Icon type="video-camera" />,
    },    
    {
        // Download
        type: [5],
        typeName: "Download",
        icon: <Icon type="download" />,
    },
]

interface IContentResourceTypeProps {
    resource: ContentReourceModel;
    onResourceSelect: any;
}


export const ContentResourceType: React.FC<IContentResourceTypeProps> = ({resource, onResourceSelect}) => {
    const renderContent = (resource: ContentReourceModel) => {
        const contentDomain = (GLGlobal.processEnv().portals as any).resources;
        return <ul className="chat-content-resource-container">
            {
                contentResourceList.map(item => {
                    // ponytail: skip empty/blank URLs so a later valid link of the same media type wins
                    const model = (resource.links || []).find(x => item.type.includes(x.sharableMediaType) && x.url && x.url.trim());
                    if (model) {
                        let content = {...resource};
                        content.resourceUrl = model.sharableMediaType === 5 ? `${model.url}` : `${contentDomain}${model.url}`;                        
                        return <li key={item.type[0]} className="chat-content-resource-icon"><a onClick={() => onResourceSelect(content)}>{item.icon}</a></li>;
                    }
                    return <li key={item.type[0]} className="chat-content-resource-icon-illegal">{item.icon}</li>;
                })
            }
        </ul>
    }
    return (
        <div>{renderContent(resource)}</div>
    );
}

interface ISearchableListProps {
    onSearch: any;
    getTitle: (item: any) => any;
    getDescription: (item: any) => any;
    getContent?: (item: any) => any;
    content: any;
    loading: boolean;
    hasMore?: boolean;
    onLoadMore?: () => void;
}

export const SearchableList: React.FC<ISearchableListProps> = ({
    onSearch,
    getTitle,
    getDescription,
    getContent,
    content,
    loading,
    hasMore = false,
    onLoadMore = () => { },
}) => {
    const { Search } = Input

    return (
        <div>
            <Search placeholder={fmtMsg({id: GSAdminLocale.ResourceSearch})} onSearch={onSearch} className="chat-resource-search"/>
            <div className="chat-resource-content">
                <InfiniteScroll
                    initialLoad={false}
                    pageStart={0}
                    loadMore={onLoadMore}
                    hasMore={!loading && hasMore}
                    useWindow={false}
                >
                    <List
                        dataSource={content}
                        renderItem={(item: any) => (
                            <List.Item key={item.name}>
                                <List.Item.Meta
                                    title={getTitle(item)}
                                    description={getDescription(item)}
                                />
                                {
                                    getContent &&
                                    <div>{getContent(item)}</div>
                                }
                            </List.Item>
                        )}
                    >
                        {loading && (
                            <div className="chat-searchable-list-spin">
                                <Spin />
                            </div>
                        )}
                    </List>
                </InfiniteScroll>
            </div>
        </div>
    );
}
